/**
 * waveform-cache.ts
 *
 * Two-level cache for decoded waveform peak data.
 *
 * Level 1 — In-memory Map (module singleton)
 *   • Survives React re-mounts and client-side navigation within one session.
 *   • Cleared when the browser tab is closed / page is hard-refreshed.
 *   • Read/write is synchronous and instant.
 *
 * Level 2 — IndexedDB ("bb-waveforms" database)
 *   • Persists across page reloads and browser restarts.
 *   • Stores the 200-bar Float32Array peak data as two raw ArrayBuffers.
 *   • ~1.6 KB per track — effectively unlimited storage.
 *   • Falls back gracefully (non-fatal) if IDB is unavailable (e.g. Safari
 *     private mode with IDB blocked).
 *
 * Usage in TrackWaveform:
 *   1. Check getMemWaveform(url) → if hit, use immediately (no loading state)
 *   2. Check getIdbWaveform(url) → if hit, warm memCache and use (fast)
 *   3. Otherwise: fetch + decode → then call saveWaveform(url, L, R)
 */

const DB_NAME    = "bb-waveforms";
const STORE_NAME = "peaks";
const DB_VERSION = 1;

// ─── In-memory cache ──────────────────────────────────────────────────────────

interface WaveformEntry {
  peaksL: Float32Array;
  peaksR: Float32Array;
}

const memCache = new Map<string, WaveformEntry>();

/** Synchronous memory-cache lookup. Returns null on miss. */
export function getMemWaveform(url: string): WaveformEntry | null {
  return memCache.get(url) ?? null;
}

/** Write to the in-memory cache. */
export function setMemWaveform(
  url: string,
  peaksL: Float32Array,
  peaksR: Float32Array,
): void {
  memCache.set(url, { peaksL, peaksR });
}

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror  = () => { _dbPromise = null; reject(req.error); };
  });
  return _dbPromise;
}

/** Async IndexedDB lookup. Warms the memCache on hit. Returns null on miss or error. */
export async function getIdbWaveform(url: string): Promise<WaveformEntry | null> {
  try {
    const db = await openDb();
    return new Promise<WaveformEntry | null>((resolve) => {
      const tx  = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(url);
      req.onsuccess = () => {
        const val = req.result as { peaksL: ArrayBuffer; peaksR: ArrayBuffer } | undefined;
        if (!val) { resolve(null); return; }
        const entry: WaveformEntry = {
          peaksL: new Float32Array(val.peaksL),
          peaksR: new Float32Array(val.peaksR),
        };
        // Warm the in-memory cache while we're here
        memCache.set(url, entry);
        resolve(entry);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null; // IDB unavailable — fail gracefully
  }
}

/** Write peaks to IndexedDB (async, non-blocking — fire and forget). */
export async function setIdbWaveform(
  url: string,
  peaksL: Float32Array,
  peaksR: Float32Array,
): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      // Store as plain ArrayBuffer — structured-cloneable, compact
      tx.objectStore(STORE_NAME).put(
        {
          peaksL: peaksL.buffer.slice(
            peaksL.byteOffset,
            peaksL.byteOffset + peaksL.byteLength,
          ),
          peaksR: peaksR.buffer.slice(
            peaksR.byteOffset,
            peaksR.byteOffset + peaksR.byteLength,
          ),
        },
        url,
      );
      tx.oncomplete = () => resolve();
      tx.onerror    = () => resolve(); // non-fatal
    });
  } catch {
    /* IDB unavailable — non-fatal */
  }
}

/**
 * Convenience: write to BOTH caches in one call.
 * IDB write is async/non-blocking; mem write is synchronous.
 */
export function saveWaveform(
  url: string,
  peaksL: Float32Array,
  peaksR: Float32Array,
): void {
  setMemWaveform(url, peaksL, peaksR);
  setIdbWaveform(url, peaksL, peaksR); // intentionally not awaited
}
